import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { sbomApi } from './sbomApi';
import { formatStampShort } from '../utils/datetime';
import { BAND_STYLE, BandHelp, CONTROL_STYLE, health, HelpPop, Tip, TrendDelta } from '../pages/risk/riskUi';
import { Modal, SearchSelect } from '../pages/review/reviewUi';
import { TraceDashboardMini, useDashboardBuild } from '../components/TraceDashboard';
import { traceDashApi } from '../utils/traceDashboardApi';
import '../styles/DocReview.css';
import '../styles/Risk.css';
import '../styles/TraceDashboard.css';
import './SbomDashboard.css';

/**
 * SBOM Dashboard (v4.32.0) — the Traceability / Risk dashboard, for vulnerabilities. Mockup
 * https://claude.ai/artifact/1eFCnq8NmL1vYwwtgPTtVT (board 2: one card per release; board 1: the
 * full view). Score · assessed by severity · what would lower it · status tiles · one grid row per
 * component version with the action that helps it most. Computed on the server on each read
 * (sbom_dashboard_engine.py); the grid is filtered and paged there (Critical Note #38).
 */

const PAGE = 25;

export const SEV_COLOR = { Critical: '#7A1F05', High: '#C2410C', Medium: '#D49B2A', Low: '#A8A1BE', Unknown: '#D4D0E0' };
const SEVS = ['Critical', 'High', 'Medium', 'Low', 'Unknown'];
const STATUS_ORDER = ['unassessed', 'open', 'kev', 'controlled', 'not_affected', 'fixable'];
const STATUS_DOT = {
  kev: '#7A1F05', open: '#C2410C', unassessed: '#D49B2A', controlled: '#17603A', not_affected: '#3B6FB6', fixable: '#A8A1BE',
};
export const STATUS_PILL = {
  kev: { background: '#7A1F05', color: '#fff' },
  open: { background: '#FBE0D5', color: '#8A2A0B' },
  unassessed: { background: '#FFF1D6', color: '#7A4B00' },
  controlled: { background: '#DDF3E4', color: '#17603A' },
  not_affected: { background: '#E3ECF8', color: '#234B85' },
  fixable: { background: '#F3F0F8', color: '#4A4560' },
};
const CONTROL_LABEL = {
  no_risk: 'No risk linked', controlled: 'Controlled', accepted: 'Accepted', mitigated: 'Mitigated', not_run: 'Not run',
  unverified: 'Unverified', failed: 'Failed', uncontrolled: 'Uncontrolled', missing: 'Not in panel',
};

// What each part counts against the score. Mirrors sbom_dashboard_engine.finding_flags / SCORE_WEIGHTS —
// change both together.
const DRIVER_HELP = {
  open: 'A finding assessed Affected whose linked risk has no passed mitigation test — or no risk linked at all.',
  unassessed: 'A finding with no verdict yet, or one still Under investigation.',
  kev: 'A finding on the CISA Known Exploited Vulnerabilities list that is not assessed Not affected or controlled.',
  fixable: 'A newer version of the component fixes the finding, and the finding is not assessed Not affected.',
};

const pct = (n, t) => (t ? Math.round((100 * n) / t) : 0);
// A release name often already ends with its version ("Lab Analyzer 2.3") — never print it twice.
export const relLabel = (r) => {
  const name = (r?.name || '').trim();
  const v = (r?.version || '').trim();
  return v && !name.endsWith(v) ? `${name} ${v}` : name;
};

export function ExposureScore({ score, trend }) {
  if (!score) return <div className="rsk-dim">Nothing to score yet.</div>;
  return (
    <div className="rsk-score">
      <div className="rsk-lbl rsk-score-head">
        <span>Exposure score</span>
        <HelpPop label="How the exposure score works">
          <p className="rsk-help-lead">
            <strong>Higher is better.</strong> 100 means every finding is assessed Not affected or its risk is
            controlled; the score falls as unresolved findings add up, fastest for Critical ones.
          </p>
          <BandHelp />
          <table className="rsk-help-tbl">
            <thead><tr><th>Part</th><th>What counts against you</th><th>Points lost</th></tr></thead>
            <tbody>
              {score.drivers.map((d) => (
                <tr key={d.key}>
                  <td>{d.label}</td>
                  <td>{DRIVER_HELP[d.key]}</td>
                  <td className="rsk-help-num"><strong>−{d.points}</strong> / {d.max}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <p className="rsk-fine">
            Each finding is weighted by severity (Critical 1 · High 0.7 · Medium 0.4 · Low 0.15). A part fills up
            towards its weight as findings add up — about two-thirds full at three unresolved Critical findings — so
            many clean components never water down a real problem.
          </p>
        </HelpPop>
      </div>
      <div className="rsk-score-main">
        <div className="rsk-score-num">{health(score.score)}</div>
        <div className="rsk-score-of">
          <div>of 100 · higher is better</div>
          <span className="rsk-pill" style={BAND_STYLE[score.band]}>{score.band}</span>
        </div>
        {trend?.length > 0 && (
          <div className="rsk-trend" aria-label="Score at each baseline">
            {trend.slice(-3).map((t) => <div key={t.id}>{t.name} · <strong>{health(t.score) ?? '—'}</strong></div>)}
            <div>Now · <strong>{health(score.score)}</strong><TrendDelta from={trend[trend.length - 1]?.score} to={score.score} /></div>
          </div>
        )}
      </div>
      <div className="rsk-drivers">
        {score.drivers.map((d) => (
          <div className="rsk-drv sbd-drv" key={d.key}>
            <span>{d.label}</span>
            <div className="rsk-trk" role="img" aria-label={`${d.label} ${d.points} of ${d.max} points lost`}>
              <i style={{ width: `${d.max ? (d.points / d.max) * 100 : 0}%` }} />
            </div>
            <span className="rsk-drv-pts"><strong>−{d.points}</strong> / {d.max}</span>
          </div>
        ))}
      </div>
      <div className="rsk-fine">
        Weights: affected and not controlled 40 · not assessed 25 · actively exploited 20 · fix not applied 15. A
        finding assessed <strong>Not affected</strong> never counts against you.
      </div>
    </div>
  );
}

/**
 * Findings by severity: each bar's LENGTH is the severity's finding count (against the largest), its
 * solid part the assessed ones and its pale part the rest. A bar of assessed share alone read as a row
 * of empty 0% bars on every release nobody had assessed yet — true, and hiding the counts that matter.
 * The Controlled row is the share of Affected findings whose risk chain passed.
 */
export function AssessedBySeverity({ severity, controlled, compact = false }) {
  if (!severity?.length) return <div className="rsk-dim">No findings in this release.</div>;
  const max = Math.max(...severity.map((s) => s.total), 1);
  return (
    <div className={`tdb-levels${compact ? ' tdb-levels--compact' : ''}`}>
      {severity.map((l) => {
        const p = pct(l.n, l.total);
        const color = SEV_COLOR[l.name] || SEV_COLOR.Unknown;
        return (
          <div className="tdb-lvl" key={l.name}>
            <span className="tdb-lvl-name">
              <strong>{l.name}</strong>
              {!compact && <span>{l.n} of {l.total} assessed</span>}
            </span>
            <div className="rsk-trk tdb-trk sbd-sevtrk" role="img"
              aria-label={`${l.name}: ${l.total} findings, ${l.n} assessed`}>
              <span className="sbd-sevbar" style={{ width: `${Math.max(4, (100 * l.total) / max)}%` }}>
                <i style={{ width: `${p}%`, background: color }} />
                <i style={{ width: `${100 - p}%`, background: color, opacity: 0.3 }} />
              </span>
            </div>
            <span className="tdb-lvl-num"><strong>{l.total}</strong>{!compact && <> · {p}% assessed</>}</span>
          </div>
        );
      })}
      {controlled?.total > 0 && (
        <div className="tdb-lvl">
          <span className="tdb-lvl-name">
            <strong>Controlled</strong>
            {!compact && <span>affected, chain passed</span>}
          </span>
          <div className="rsk-trk tdb-trk" role="img" aria-label={`Controlled ${pct(controlled.n, controlled.total)} percent`}>
            <i className="tdb-fill--pass" style={{ width: `${pct(controlled.n, controlled.total)}%` }} />
          </div>
          <span className="tdb-lvl-num"><strong>{pct(controlled.n, controlled.total)}%</strong>{!compact && <> · {controlled.n} / {controlled.total}</>}</span>
        </div>
      )}
      <div className="sbd-sevkey"><i /> assessed <i className="sbd-sevkey-pale" /> not yet</div>
    </div>
  );
}

function SevBar({ sev }) {
  const total = SEVS.reduce((a, k) => a + (sev?.[k.toLowerCase()] || 0), 0);
  if (!total) return <span className="rsk-dim">—</span>;
  const parts = SEVS.filter((k) => sev[k.toLowerCase()]);
  return (
    <Tip className="rsk-bar-cell" heading={`${total} finding${total === 1 ? '' : 's'}`}
      tip={parts.map((k) => `${k}: ${sev[k.toLowerCase()]}`).join(' · ')}>
      <span className="rsk-bar">
        {parts.map((k) => <i key={k} style={{ width: `${(100 * sev[k.toLowerCase()]) / total}%`, background: SEV_COLOR[k] }} />)}
      </span>
      <span className="rsk-bar-num">{parts.map((k) => `${sev[k.toLowerCase()]} ${k[0]}`).join(' · ')}</span>
    </Tip>
  );
}

function AssessedBar({ n, total }) {
  const p = pct(n, total);
  const tone = p === 100 ? 'tdb-fill--pass' : p === 0 ? 'tdb-fill--gap' : 'tdb-fill--part';
  return (
    <Tip className="rsk-bar-cell" heading={`${p}% assessed`} tip={`${n} of ${total} findings have a VEX verdict.`}>
      <span className="rsk-bar"><i className={tone} style={{ width: `${Math.max(p, p ? 4 : 0)}%` }} /></span>
      <span className="rsk-bar-num">{n}/{total}</span>
    </Tip>
  );
}

// A mitigation's test state (sbom_dashboard_engine.mitigation_result).
const MIT_RESULT = {
  pass: ['passed', { background: '#DDF3E4', color: '#17603A' }],
  fail: ['failed', { background: '#FBE0D5', color: '#8A2A0B' }],
  not_run: ['not run', { background: '#F3F0F8', color: '#4A4560' }],
  no_test: ['no test', { background: '#FFF1D6', color: '#7A4B00' }],
  unknown: ['not read', { background: '#F3F0F8', color: '#6B6580' }],
};

function ControlPill({ control }) {
  const st = CONTROL_STYLE[control?.status] || CONTROL_STYLE.unscored;
  return <span className="rsk-pill sbd-ctl" style={{ background: st.bg, color: st.fg }}>{control?.label || CONTROL_LABEL[control?.status] || 'Not read'}</span>;
}

/** One linked risk: key (opens in Orcanos) + control, its name, then each mitigation with its test state. */
function RiskChain({ risk }) {
  return (
    <div className="sbd-risk">
      <div className="sbd-risk-head">
        {risk.url
          ? <a className="rsk-key rsk-key--link" href={risk.url} target="_blank" rel="noopener noreferrer">{risk.key} ↗</a>
          : <span className="rsk-key">{risk.key}</span>}
        <ControlPill control={risk.control} />
      </div>
      {risk.name && <div className="sbd-risk-name" title={risk.name}>{risk.name}</div>}
      {risk.missing && <div className="sbd-risk-note">No longer in the Risk panel&apos;s filter</div>}
      {(risk.mitigations || []).map((m) => (
        <div className="sbd-mit" key={m.key || m.name}>
          <span className="sbd-mit-arrow" aria-hidden="true">↳</span>
          <span className="sbd-mit-key" title={m.name}>{m.key || m.name}</span>
          <span className="rsk-pill sbd-mit-res" style={MIT_RESULT[m.result]?.[1]}>{MIT_RESULT[m.result]?.[0] || m.result}</span>
        </div>
      ))}
      {!risk.missing && risk.mitigations?.length === 0 && risk.control?.status && (
        <div className="sbd-risk-note">No mitigation traced</div>
      )}
    </div>
  );
}

/**
 * The grid's Risk → mitigation cell (user request 2026-10-23, replaces the bare control pill): every
 * risk the component's Affected findings link to, worst control first, each with its mitigations and
 * their tests. More than two → "+N more" expands in place.
 */
function RiskCell({ cell }) {
  const [all, setAll] = useState(false);
  const risks = cell?.risks || [];
  if (!risks.length && !cell?.no_risk && !cell?.protected_by?.length) return <span className="rsk-dim">—</span>;
  const shown = all ? risks : risks.slice(0, 2);
  return (
    <div className="sbd-riskcell">
      {shown.map((r) => <RiskChain key={r.key} risk={r} />)}
      {risks.length > 2 && (
        <button type="button" className="drv-link rsk-small-link sbd-more" onClick={() => setAll((x) => !x)}>
          {all ? 'Show less' : `+${risks.length - 2} more`}
        </button>
      )}
      {cell.no_risk > 0 && (
        <span className="rsk-pill" style={{ background: '#FFF1D6', color: '#7A4B00' }}>
          {cell.no_risk} Affected with no risk
        </span>
      )}
      {cell.protected_by?.length > 0 && (
        <div className="sbd-risk-note">Not affected — protected by {cell.protected_by.join(', ')}</div>
      )}
    </div>
  );
}

/** The grid's Recommendation cell: the one action that lowers this component's points most. */
function RecCell({ row, rid, onStudio }) {
  const navigate = useNavigate();
  const r = row.rec || {};
  const toVuln = (vid) => navigate(`/bom/sw-vulns?release=${encodeURIComponent(rid)}&locate=${encodeURIComponent(`${vid}|${row.package_key}`)}`);
  let action = null;
  if (row.ai_drafts > 0 && r.kind === 'assess') {
    action = <button type="button" className="drv-btn sbd-act" onClick={() => onStudio('vex')}>Review AI draft{row.ai_drafts > 1 ? 's' : ''}</button>;
  } else if (r.kind === 'assess' || r.kind === 'link_risk' || r.kind === 'control') {
    action = (
      <button type="button" className="drv-btn sbd-act" onClick={() => toVuln(r.vuln_id)}>
        {r.kind === 'assess' ? 'Assess' : r.kind === 'link_risk' ? 'Link risk' : 'Open chain'}
      </button>
    );
  } else if (r.kind === 'upgrade') {
    action = <button type="button" className="drv-btn sbd-act" onClick={() => onStudio('upgrade')}>Plan upgrade</button>;
  }
  return (
    <div className="sbd-rec">
      <strong>{r.label}{row.ai_drafts > 0 && <span className="sbd-ai" title="SBOM AI Studio has drafts for this component">AI</span>}</strong>
      <span>{r.why}</span>
      {action && <div>{action}</div>}
    </div>
  );
}

function DetailGrid({ rows, rid }) {
  const navigate = useNavigate();
  return (
    <div className="rsk-table-wrap rsk-table-wrap--reg">
      <table className="rsk-table" style={{ minWidth: 1100 }}>
        <thead>
          <tr><th>Vulnerability</th><th>Component · version</th><th>Severity</th><th>Verdict</th><th>Risk → mitigation</th><th>Fixed in</th><th>Status</th></tr>
        </thead>
        <tbody>
          {(rows?.rows || []).map((f) => (
            <tr key={`${f.vuln_id}-${f.component_id}`} className="rsk-tr"
              onClick={() => navigate(`/bom/sw-vulns?release=${encodeURIComponent(rid)}&locate=${encodeURIComponent(`${f.vuln_id}|${f.package_key}`)}`)}>
              <td>
                <span className="rsk-key">{f.vuln_id}</span>{f.kev && <span className="sbd-kev">KEV</span>}
                <div className="rsk-name sbd-clip" title={f.summary}>{f.summary}</div>
              </td>
              <td>{f.component} <span className="rsk-sub">{f.version}</span></td>
              <td className="rsk-nowrap"><i className="sbd-dot" style={{ background: SEV_COLOR[f.severity] }} />{f.severity}{f.score ? ` ${f.score}` : ''}</td>
              <td className="rsk-sub">{f.label}</td>
              <td onClick={(e) => e.stopPropagation()}>
                {f.risk ? <RiskChain risk={f.risk} />
                  : f.mitigation_key ? <span className="sbd-risk-note">Protected by {f.mitigation_key}</span>
                    : f.state === 'affected' ? <span className="rsk-pill" style={{ background: '#FFF1D6', color: '#7A4B00' }}>No risk linked</span>
                      : <span className="rsk-dim">—</span>}
              </td>
              <td className="rsk-sub">{f.fixed || '—'}</td>
              <td><span className="rsk-pill" style={STATUS_PILL[f.status]}>{rows.labels?.[f.status] || f.status}</span></td>
            </tr>
          ))}
          {rows && rows.rows.length === 0 && <tr><td colSpan={7} className="rsk-dim" style={{ textAlign: 'center', padding: 24 }}>No findings match.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

export function SbomBaselineModal({ rid, readAt, onClose, onTaken }) {
  const [name, setName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    if (!name.trim()) { setError('Give the baseline a name, e.g. “Pre-submission”.'); return; }
    setBusy(true);
    setError('');
    try {
      const r = await sbomApi.takeDashBaseline(rid, { name: name.trim(), note: note.trim() });
      onTaken(r.baseline);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Take baseline"
      subtitle={`A read-only snapshot of this release's vulnerabilities as of the last check${readAt ? ` (${formatStampShort(readAt)})` : ''}: every finding, its VEX verdict, its risk control and the score. Run Check now first if you need the very latest advisories.`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="drv-btn drv-btn--lg" onClick={onClose}>Cancel</button>
          <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Take baseline'}</button>
        </>
      )}
    >
      <label className="drv-field">
        <span className="drv-lbl">Name</span>
        <input className="drv-input" value={name} onChange={(e) => { setName(e.target.value); setError(''); }}
          placeholder="e.g. Pre-submission · Release candidate 2" maxLength={120} autoFocus />
      </label>
      <label className="drv-field">
        <span className="drv-lbl">Note <span>— optional</span></span>
        <textarea className="drv-input rsk-textarea" rows={3} value={note} onChange={(e) => setNote(e.target.value)}
          placeholder="e.g. Cybersecurity review before the 510(k) submission" maxLength={1000} />
      </label>
      <p className="drv-small" style={{ margin: 0 }}>Baselines can be archived but never edited or deleted. Each one carries a SHA-256 of its content.</p>
      {error && <div className="drv-error" role="alert">{error}</div>}
    </Modal>
  );
}

/** Full view of one release (mockup board 1). */
function ReleaseDashboard({ rid, onAuth }) {
  const navigate = useNavigate();
  const [sum, setSum] = useState(null);
  const [error, setError] = useState('');
  const [status, setStatus] = useState('');
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState(null);
  const [view, setView] = useState(() => { try { return localStorage.getItem('sbom.dash.view') === 'detail' ? 'detail' : 'summary'; } catch { return 'summary'; } });
  const [taking, setTaking] = useState(false);
  const [checking, setChecking] = useState(false);
  const token = useRef(0);
  const gridRef = useRef(null);

  const load = useCallback(async () => {
    try {
      setError('');
      setSum(await sbomApi.dashboard(rid));
    } catch (e) {
      if (!onAuth(e)) setError(e.message);
    }
  }, [rid, onAuth]);
  useEffect(() => { setSum(null); setStatus(''); setSearch(''); setPage(1); load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => { setQuery(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    if (!sum) return;
    const mine = ++token.current;
    sbomApi.dashboardRows(rid, { page, page_size: PAGE, status, q: query, detail: view === 'detail' ? 1 : '' })
      .then((d) => { if (mine === token.current) setRows({ ...d, labels: sum.summary.status_labels }); })
      .catch((e) => { if (mine === token.current && !onAuth(e)) setError(e.message); });
  }, [rid, sum, page, status, query, view, onAuth]);

  // A running check: poll its status, re-read when it ends.
  useEffect(() => {
    if (!checking) return undefined;
    const t = setInterval(async () => {
      try {
        const st = await sbomApi.vulnStatus();
        if (!st.running) { setChecking(false); load(); }
      } catch { setChecking(false); }
    }, 2000);
    return () => clearInterval(t);
  }, [checking, load]);

  const checkNow = async () => {
    try { await sbomApi.scan(); setChecking(true); } catch (e) { if (!onAuth(e)) setError(e.message); }
  };
  const pickView = (v) => { setView(v); try { localStorage.setItem('sbom.dash.view', v); } catch { /* per-viewer only */ } };
  const pickStatus = (k) => { setStatus((cur) => (cur === k ? '' : k)); setPage(1); };
  const openStudio = (task) => navigate(`/bom/sw-studio?release=${encodeURIComponent(rid)}${task ? `&task=${task}` : ''}`);
  const showInGrid = (name) => {
    setStatus('');
    setSearch(name);
    gridRef.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });     // Note #113
  };

  const s = sum?.summary;
  const rel = s?.release;
  return (
    <div className="drv-page rsk-page sbd-page">
      <div className="rsk-crumbs">
        <Link to="/bom/sw-dashboard">Software BOM · Dashboard</Link><span>/</span><strong>{relLabel(rel) || 'Release'}</strong>
      </div>
      <div className="drv-head">
        <div className="sbd-head-left">
          <h1 className="drv-title">{relLabel(rel) || 'Release'}</h1>
          {sum?.releases?.length > 1 && (
            <label className="sbd-rel">
              <span>Release</span>
              <select className="drv-select" value={rid} onChange={(e) => navigate(`/bom/sw-dashboard/${e.target.value}`)}>
                {sum.releases.map((r) => <option key={r.item_id} value={r.item_id}>{relLabel(r)}</option>)}
              </select>
            </label>
          )}
        </div>
        <div className="rsk-actions">
          <button type="button" className="drv-btn" onClick={() => navigate(`/bom/sw-dashboard/${rid}/baselines`)}>
            Baselines{sum?.trend?.length ? ` · ${sum.trend.length}` : ''}
          </button>
          <button type="button" className="drv-btn drv-btn--primary" onClick={() => openStudio('')}>SBOM AI Studio</button>
        </div>
      </div>
      {error && <div className="drv-error" role="alert">{error}</div>}
      {sum?.risk_note && <div className="drv-muted">{sum.risk_note}</div>}

      <section className="rsk-card" aria-label="SBOM dashboard">
        <div className="rsk-card-head">
          <div style={{ minWidth: 0 }}>
            <h2 className="rsk-card-title">Vulnerability exposure and assessment</h2>
            <div className="rsk-card-meta">
              {s ? <>{s.n_components} components · {s.n_findings} finding{s.n_findings === 1 ? '' : 's'} in {s.n_affected_components} component{s.n_affected_components === 1 ? '' : 's'} · OSV.dev + CISA KEV</> : 'Loading…'}
              {sum?.read_at ? <> · checked {formatStampShort(sum.read_at)}</> : sum ? ' · not checked yet' : null}
            </div>
          </div>
          <div className="rsk-actions">
            <button type="button" className="drv-btn" onClick={() => setTaking(true)} disabled={!s}>Take baseline</button>
            <button type="button" className="rsk-iconbtn" onClick={checkNow} disabled={checking || sum?.scan?.running}
              title={checking ? 'Checking…' : 'Check every component for vulnerabilities now'} aria-label="Check now">
              <span className={checking ? 'rsk-spin' : undefined}>⟳</span>
            </button>
          </div>
        </div>
        {!sum && !error && <div className="drv-muted" style={{ padding: 18 }}>Loading…</div>}

        {s && (
          <>
            <div className="rsk-grid3">
              <div className="rsk-cell"><ExposureScore score={s.score} trend={sum.trend} /></div>
              <div className="rsk-cell">
                <div className="rsk-lbl">Findings by severity</div>
                <AssessedBySeverity severity={s.severity} controlled={s.controlled} />
                <div className="rsk-fine">
                  Bar length: how many findings of each severity; solid part: those with a VEX verdict. <strong>Controlled</strong>: Affected findings whose
                  risk → mitigation → test chain passed (status from the Risk module).
                </div>
              </div>
              <div className="rsk-cell">
                <div className="rsk-lbl rsk-score-head">
                  <span>What would raise the score most</span>
                  <HelpPop label="How this list works" end>
                    <p className="rsk-help-lead"><strong>The five components that take the most off the score.</strong> Fix one and the score rises by its number.</p>
                    <p className="rsk-fine">The line under each is its recommended action — the one that raises the score most: upgrade to a fixed version, assess, link a risk, or close a risk control.</p>
                  </HelpPop>
                </div>
                {(s.score?.lower || []).length === 0 && <div className="rsk-dim">Nothing — every finding is resolved.</div>}
                {(s.score?.lower || []).map((l) => (
                  <button type="button" key={l.id} className="rsk-lower" onClick={() => showInGrid(l.key.split(' ')[0])} title="Show it in the grid">
                    <span className="rsk-lower-key">{l.key}</span>
                    <span className="rsk-lower-name">{l.name}<span>{l.why}</span></span>
                    <span className="rsk-lower-pts">+{l.points}</span>
                  </button>
                ))}
                {s.status?.unassessed > 0 && (
                  <button type="button" className="drv-link tdb-studio-link" onClick={() => openStudio('vex')}>
                    {s.drafts > 0 ? `Review ${s.drafts} AI draft${s.drafts === 1 ? '' : 's'} in SBOM AI Studio →`
                      : `Draft the ${s.status.unassessed} assessment${s.status.unassessed === 1 ? '' : 's'} in SBOM AI Studio →`}
                  </button>
                )}
              </div>
            </div>

            <div className="rsk-band">
              <div className="rsk-lbl">Finding status — component → vulnerability → VEX verdict → risk control</div>
              <div className="rsk-tiles">
                {STATUS_ORDER.map((k) => {
                  const on = status === k;
                  return (
                    <button type="button" key={k} className={`rsk-tile${on ? ' rsk-tile--on' : ''}`} onClick={() => pickStatus(k)} aria-pressed={on}>
                      <span className="rsk-tile-name"><i style={{ background: STATUS_DOT[k] }} />{s.status_labels?.[k] || k}</span>
                      <span className="rsk-tile-n">{s.status?.[k] || 0}</span>
                      <span className="rsk-tile-hint">{s.status_hints?.[k]}</span>
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="rsk-reg-bar" ref={gridRef}>
              <div className="rsk-lbl">
                {view === 'detail' ? 'Findings' : 'Components with findings'}
                {status && <button type="button" className="drv-link rsk-small-link" onClick={() => pickStatus(status)}>clear filter ({s.status_labels?.[status]})</button>}
              </div>
              <div className="rsk-reg-tools">
                <div className="rsk-seg" role="group" aria-label="Grid view">
                  <button type="button" className={view === 'summary' ? 'on' : ''} aria-pressed={view === 'summary'} onClick={() => pickView('summary')}>Summary</button>
                  <button type="button" className={view === 'detail' ? 'on' : ''} aria-pressed={view === 'detail'} onClick={() => pickView('detail')}>Detail</button>
                </div>
                <input type="search" className="drv-input rsk-search" aria-label="Search components or vulnerabilities"
                  placeholder="Search component or CVE" value={search} onChange={(e) => setSearch(e.target.value)} />
              </div>
            </div>

            {view === 'detail' ? <DetailGrid rows={rows} rid={rid} /> : (
              <div className="rsk-table-wrap rsk-table-wrap--reg">
                <table className="rsk-table" style={{ minWidth: 1180 }}>
                  <thead>
                    <tr><th>Component · version</th><th>Findings by severity</th><th>Assessed</th><th>Risk → mitigation</th><th>Status</th><th>Recommendation</th><th className="sbd-num">Points lost</th></tr>
                  </thead>
                  <tbody>
                    {(rows?.rows || []).map((r) => (
                      <tr key={r.id}>
                        <td>
                          <span className="rsk-key">{r.name}</span> <span className="rsk-sub">{r.version}</span>
                          {r.kev && <span className="sbd-kev">KEV</span>}
                          <div className="rsk-name sbd-purl">{r.key ? `${r.key} · ` : ''}{r.purl}</div>
                        </td>
                        <td><SevBar sev={r.sev} /></td>
                        <td><AssessedBar n={r.assessed} total={r.n} /></td>
                        <td><RiskCell cell={r.risk} /></td>
                        <td><span className="rsk-pill" style={STATUS_PILL[r.status]}>{s.status_labels?.[r.status] || r.status}</span></td>
                        <td><RecCell row={r} rid={rid} onStudio={openStudio} /></td>
                        <td className="sbd-num"><strong>{r.contrib > 0 ? `−${r.contrib.toFixed(1)}` : '0'}</strong></td>
                      </tr>
                    ))}
                    {rows && rows.rows.length === 0 && (
                      <tr><td colSpan={7} className="rsk-dim" style={{ textAlign: 'center', padding: 24 }}>
                        {s.n_findings ? 'No components match.' : 'No vulnerabilities found in this release.'}
                      </td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            )}
            <div className="rsk-pager">
              <span>{rows?.total ? `${(rows.page - 1) * PAGE + 1}–${Math.min(rows.page * PAGE, rows.total)} of ${rows.total}` : '0 rows'}</span>
              <button type="button" className="drv-btn" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</button>
              <button type="button" className="drv-btn" disabled={!rows || page >= rows.pages} onClick={() => setPage((p) => p + 1)}>Next</button>
              <span className="tdb-legend">
                {SEVS.slice(0, 4).map((k) => <span key={k} className="sbd-legend"><i style={{ background: SEV_COLOR[k] }} />{k}</span>)}
                · Recommendation: the one action that wins back the most points
              </span>
            </div>
          </>
        )}
      </section>
      {taking && (
        <SbomBaselineModal rid={rid} readAt={sum?.read_at} onClose={() => setTaking(false)}
          onTaken={() => { setTaking(false); load(); }} />
      )}
    </div>
  );
}

/** One card per release (mockup board 2). */
function ReleaseCard({ c }) {
  const navigate = useNavigate();
  const rid = c.release.item_id;
  const prev = c.baseline;
  const tiles = [['unassessed', 'To assess'], ['open', 'Open'], ['kev', 'KEV'], ['controlled', 'Controlled']];
  return (
    <section className="rsk-card sbd-card" aria-label={relLabel(c.release)}>
      <div className="sbd-card-head">
        <div style={{ minWidth: 0 }}>
          <div className="sbd-card-title">{relLabel(c.release)}</div>
          <div className="rsk-fine">{c.n_components} components · {c.n_findings} finding{c.n_findings === 1 ? '' : 's'}</div>
        </div>
        <button type="button" className="drv-btn" onClick={() => navigate(`/bom/sw-releases?release=${rid}`)} title="Open the release's component tree">Release</button>
      </div>
      <div className="tdb-mini">
        <div className="tdb-mini-top">
          <div className="tdb-mini-score">
            <div className="rsk-lbl">Exposure score</div>
            <div className="tdb-mini-num">
              <strong>{health(c.score.score)}</strong>
              <span>
                of 100
                {prev && prev.score !== null && prev.score !== c.score.score && (
                  <><br /><TrendDelta from={prev.score} to={c.score.score} /> since {prev.name}</>
                )}
              </span>
            </div>
            <span className="rsk-pill" style={BAND_STYLE[c.score.band]}>{c.score.band}</span>
          </div>
          <div className="tdb-mini-cov">
            <div className="rsk-lbl">Findings by severity</div>
            <AssessedBySeverity severity={c.severity} controlled={c.controlled} compact />
          </div>
        </div>
        <div className="tdb-mini-tiles">
          {tiles.map(([k, label]) => (
            <div className="tdb-mt" key={k}>
              <span className="tdb-mt-name"><i style={{ background: STATUS_DOT[k] }} />{label}</span>
              <span className="tdb-mt-n">{c.status?.[k] || 0}</span>
            </div>
          ))}
        </div>
        {c.top && (
          <div className="tdb-mini-fix">
            <span className="rsk-lbl">Top fix</span>
            <span className="tdb-mini-fix-key">{c.top.key}</span>
            <span className="tdb-mini-fix-why" title={`${c.top.name} — ${c.top.why}`}>{c.top.name}</span>
            <strong>+{c.top.points}</strong>
          </div>
        )}
        <div className="sbd-card-foot">
          <span className="rsk-fine">{c.imported_at ? `Imported ${formatStampShort(c.imported_at)}` : ''}</span>
          <button type="button" className="drv-btn drv-btn--primary" onClick={() => navigate(`/bom/sw-dashboard/${rid}`)}>Full view</button>
        </div>
      </div>
    </section>
  );
}

/**
 * A Traceability panel whose level 1 is the vulnerability type, as its Traceability Dashboard card:
 * the panel's own cache and build (one engine, Critical Note #108) — read once, never auto-built.
 */
function VulnPanelCard({ panel, onRemove, onAuth }) {
  const navigate = useNavigate();
  const [dash, setDash] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const load = useCallback(() => {
    setLoading(true);
    traceDashApi.summary(panel.id)
      .then((d) => { setDash(d); setError(''); })
      .catch((e) => { if (!onAuth(e.status === 401 ? { httpCode: 401 } : e)) setError(e.message); })   // traceDashApi sets .status
      .finally(() => setLoading(false));
  }, [panel.id, onAuth]);
  useEffect(() => { load(); }, [load]);
  const b = useDashboardBuild(panel.id, load);
  const openFull = () => {
    try { sessionStorage.setItem('matrixViewMode', 'dashboard'); } catch { /* ignore */ }
    navigate(`/trace/matrix/${panel.id}`);
  };
  return (
    <section className="rsk-card sbd-card" aria-label={panel.name}>
      <div className="sbd-card-head">
        <div style={{ minWidth: 0 }}>
          <div className="sbd-card-title">{panel.name}</div>
          <div className="rsk-fine">Traceability panel · level 1: {panel.l1 || '—'}</div>
        </div>
        <div className="rsk-actions">
          <button type="button" className="drv-btn" onClick={b.build} disabled={b.busy}
            title="Rebuild the dashboard from the full matrix">{b.busy ? 'Building…' : dash?.cached ? '↻ Rebuild' : '↻ Build'}</button>
          <button type="button" className="rsk-iconbtn" onClick={() => onRemove(panel)}
            title="Remove from this dashboard (the panel itself stays)" aria-label={`Remove ${panel.name}`}>×</button>
        </div>
      </div>
      <TraceDashboardMini dash={dash} loading={loading} error={b.error || error} progress={b.progress}
        message={b.message} onBuild={b.build} />
      <div className="sbd-card-foot sbd-panel-foot">
        <span />
        <button type="button" className="drv-btn drv-btn--primary" onClick={openFull}>Full view</button>
      </div>
    </section>
  );
}

function AddPanelModal({ onClose, onAdded, onAuth }) {
  const [data, setData] = useState(null);
  const [pick, setPick] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    sbomApi.dashPanelCandidates().then(setData).catch((e) => { if (!onAuth(e)) setError(e.message); });
  }, [onAuth]);
  const free = (data?.panels || []).filter((p) => !p.added);
  const typeName = data?.vuln_label || data?.vuln_type || 'vulnerability';
  const add = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      const r = await sbomApi.addDashPanel(pick);
      onAdded(r.panel);
    } catch (e) {
      if (!onAuth(e)) setError(e.message);
      setBusy(false);
    }
  };
  return (
    <Modal
      title="Add vulnerability panel"
      subtitle={`A Traceability panel whose level 1 is ${typeName}. Its dashboard shows here as a card.`}
      onClose={onClose}
      footer={(
        <>
          <button type="button" className="drv-btn drv-btn--lg" onClick={onClose}>Cancel</button>
          <button type="button" className="drv-btn drv-btn--primary drv-btn--lg" onClick={add} disabled={!pick || busy}>{busy ? 'Adding…' : 'Add panel'}</button>
        </>
      )}
    >
      {!data && !error && <div className="drv-muted">Loading panels…</div>}
      {data && free.length > 0 && (
        <label className="drv-field">
          <span className="drv-lbl">Panel</span>
          <SearchSelect ariaLabel="Panel" placeholder="Search panels…" value={pick} onChange={(v) => setPick(String(v || ''))}
            options={free.map((p) => ({ value: p.id, label: p.name }))
              .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }))} />
        </label>
      )}
      {data && free.length === 0 && (
        <p className="drv-small" style={{ margin: 0 }}>
          {data.panels.length ? 'Every matching panel is already on the dashboard.'
            : `No Traceability panel you can see starts at ${typeName}. Create one under Traceability with ${typeName} as level 1, then add it here.`}
          {!data.vuln_type && ' Tip: set the Vulnerability work item type in Settings → Software BOM so panels are matched by type.'}
        </p>
      )}
      {error && <div className="drv-error" role="alert">{error}</div>}
    </Modal>
  );
}

function DashboardHome({ onAuth }) {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const [panels, setPanels] = useState([]);
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    sbomApi.dashPanels().then((d) => setPanels(d.panels || [])).catch((e) => { if (!onAuth(e)) setError(e.message); });
  }, [onAuth]);
  const removePanel = async (p) => {
    try {
      await sbomApi.removeDashPanel(p.id);
      setPanels((cur) => cur.filter((x) => x.id !== p.id));
    } catch (e) { if (!onAuth(e)) setError(e.message); }
  };
  const load = useCallback(() => {
    sbomApi.dashboardHome().then((d) => { setData(d); setError(''); }).catch((e) => { if (!onAuth(e)) setError(e.message); });
  }, [onAuth]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!checking) return undefined;
    const t = setInterval(async () => {
      try { const st = await sbomApi.vulnStatus(); if (!st.running) { setChecking(false); load(); } } catch { setChecking(false); }
    }, 2000);
    return () => clearInterval(t);
  }, [checking, load]);
  const checkNow = async () => {
    try { await sbomApi.scan(); setChecking(true); } catch (e) { if (!onAuth(e)) setError(e.message); }
  };
  const kev = (data?.releases || []).reduce((a, c) => a + (c.status?.kev || 0), 0);
  return (
    <div className="drv-page rsk-page sbd-page">
      <div className="drv-head">
        <div>
          <h1 className="drv-title">Dashboard</h1>
          <p className="drv-sub">
            One card per release
            {data?.read_at ? ` · last vulnerability check ${formatStampShort(data.read_at)}` : data ? ' · not checked yet' : ''}
            {kev ? ` · ${kev} actively exploited finding${kev === 1 ? '' : 's'} open across all releases` : ''}
          </p>
        </div>
        <div className="rsk-actions">
          <button type="button" className="drv-btn" onClick={() => setAdding(true)}>+ Add vulnerability panel</button>
          <button type="button" className="drv-btn" onClick={checkNow} disabled={checking || data?.scan?.running}>{checking ? 'Checking…' : 'Check now'}</button>
          <button type="button" className="drv-btn drv-btn--primary" onClick={() => navigate('/bom/sw-studio')}>SBOM AI Studio</button>
        </div>
      </div>
      {error && <div className="drv-error" role="alert">{error}</div>}
      {data?.risk_note && <div className="drv-muted">{data.risk_note}</div>}
      {!data && !error && <div className="drv-muted">Loading…</div>}
      {data && data.releases.length === 0 && (
        <div className="rsk-card tdb-empty">
          <p>No releases yet. Import an SBOM and its vulnerabilities show here, one card per release.</p>
          <button type="button" className="drv-btn drv-btn--primary" onClick={() => navigate('/bom/sw-import')}>Import SBOM</button>
        </div>
      )}
      <div className="sbd-cards">
        {(data?.releases || []).map((c) => <ReleaseCard key={c.release.item_id} c={c} />)}
      </div>
      {panels.length > 0 && (
        <>
          <h2 className="sbd-section">Vulnerability traceability</h2>
          <div className="sbd-cards">
            {panels.map((p) => <VulnPanelCard key={p.id} panel={p} onRemove={removePanel} onAuth={onAuth} />)}
          </div>
        </>
      )}
      {adding && (
        <AddPanelModal onClose={() => setAdding(false)} onAuth={onAuth}
          onAdded={(p) => { setAdding(false); setPanels((cur) => (cur.some((x) => x.id === p.id) ? cur : [...cur, p])); }} />
      )}
    </div>
  );
}

export default function SbomDashboard({ releaseId, onAuth }) {
  return releaseId ? <ReleaseDashboard rid={releaseId} onAuth={onAuth} /> : <DashboardHome onAuth={onAuth} />;
}
